import dayjs from 'dayjs';
import { Workbook } from 'exceljs';
import { STORAGE_REGION_LABELS, StorageRegion, StorageTable, StorageWeek } from './EiaGasStorageLogic';

export interface ExportEiaGasStorageParams {
  region: StorageRegion;
  table: StorageTable;
  includeFiveYear: boolean;
  /** Every week on record, written to a second sheet so the numbers can be reworked in Excel. */
  weeks: StorageWeek[];
  latestWeekEnding?: Date;
}

const VOLUME_FORMAT = '#,##0;(#,##0)';

/** Writes the calendar year table and the raw weekly history to an Excel workbook and downloads it. */
export async function exportEiaGasStorageToExcel(params: ExportEiaGasStorageParams): Promise<void> {
  const { region, table, includeFiveYear, weeks, latestWeekEnding } = params;
  const { year, otherYears, rows } = table;
  const regionLabel = STORAGE_REGION_LABELS[region];

  const workbook = new Workbook();
  workbook.creator = 'KnowRisk';

  const reportSheet = workbook.addWorksheet('Storage');
  reportSheet.addRow(['Working Natural Gas in Underground Storage']).font = { bold: true, size: 14 };
  reportSheet.addRow([regionLabel + (latestWeekEnding ? ` - for week ended ${dayjs(latestWeekEnding).format('MM/DD/YYYY')}` : '')]);
  reportSheet.addRow(['Stocks in billion cubic feet (Bcf). Source: EIA Weekly Natural Gas Storage Report.']);
  reportSheet.addRow([]);

  const currentLabel = `${year}`;
  const yearAgoLabel = `${year - 1} (Interpolated)`;
  const fiveYearLabel = '5 Year Avg (Interpolated)';

  const changeColumns = [currentLabel, yearAgoLabel, ...(includeFiveYear ? [fiveYearLabel] : [])];
  const stockColumns = [currentLabel, yearAgoLabel, ...(includeFiveYear ? [fiveYearLabel] : []), ...otherYears.map(otherYear => `${otherYear}`)];

  const groupHeader: (string | undefined)[] = ['Week', 'Week Ending', 'Injection / Withdrawal', ...changeColumns.slice(1).map(() => undefined), 'Total Stocks', ...stockColumns.slice(1).map(() => undefined)];
  const columnHeader: string[] = ['', '', ...changeColumns, ...stockColumns];

  const groupRow = reportSheet.addRow(groupHeader);
  groupRow.font = { bold: true };
  const changeStart = 3;
  const stockStart = changeStart + changeColumns.length;
  reportSheet.mergeCells(groupRow.number, changeStart, groupRow.number, stockStart - 1);
  reportSheet.mergeCells(groupRow.number, stockStart, groupRow.number, stockStart + stockColumns.length - 1);
  groupRow.getCell(changeStart).alignment = { horizontal: 'center' };
  groupRow.getCell(stockStart).alignment = { horizontal: 'center' };
  const headerRow = reportSheet.addRow(columnHeader);
  headerRow.font = { bold: true };

  for (const row of rows) {
    const values: (string | number | Date | null)[] = [row.weekNumber, row.weekEnding];
    values.push(row.current?.netChange ?? null, row.yearAgo?.netChange ?? null);
    if (includeFiveYear) values.push(row.fiveYear?.netChange ?? null);
    values.push(row.current?.volume ?? null, row.yearAgo?.volume ?? null);
    if (includeFiveYear) values.push(row.fiveYear?.average ?? null);
    for (const otherYear of otherYears) values.push(row.otherYears[otherYear] ?? null);

    const dataRow = reportSheet.addRow(values);
    dataRow.getCell(2).numFmt = 'mm/dd/yyyy';
    for (let column = changeStart; column <= columnHeader.length; column++) dataRow.getCell(column).numFmt = VOLUME_FORMAT;
  }

  reportSheet.getColumn(1).width = 8;
  reportSheet.getColumn(2).width = 13;
  for (let column = changeStart; column <= columnHeader.length; column++) {
    reportSheet.getColumn(column).width = 24;
  }

  const historySheet = workbook.addWorksheet('Weekly History');
  const historyHeader = historySheet.addRow(['Week Ending', 'East', 'Midwest', 'Mountain', 'Pacific', 'South Central', 'South Central Salt', 'South Central Nonsalt', 'Total Lower 48']);
  historyHeader.font = { bold: true };
  const sortedWeeks = [...weeks].sort((a, b) => a.weekEnding.localeCompare(b.weekEnding));
  for (const week of sortedWeeks) {
    const [year, month, day] = week.weekEnding.substring(0, 10).split('-').map(Number);
    const historyRow = historySheet.addRow([
      new Date(Date.UTC(year, month - 1, day)),
      week.east ?? null,
      week.midwest ?? null,
      week.mountain ?? null,
      week.pacific ?? null,
      week.southCentral ?? null,
      week.southCentralSalt ?? null,
      week.southCentralNonSalt ?? null,
      week.total,
    ]);
    historyRow.getCell(1).numFmt = 'mm/dd/yyyy';
    for (let column = 2; column <= 9; column++) historyRow.getCell(column).numFmt = '#,##0';
  }
  historySheet.getColumn(1).width = 13;
  for (let column = 2; column <= 9; column++) historySheet.getColumn(column).width = 14;
  historySheet.views = [{ state: 'frozen', ySplit: 1 }];

  const filename = `EIA Gas Storage ${regionLabel}${latestWeekEnding ? ` ${dayjs(latestWeekEnding).format('YYYY-MM-DD')}` : ''}.xlsx`;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
